import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileCode2, RefreshCw } from 'lucide-react';
import { DataTable, type Column } from '../components/data-table';
import { PageHeader, Section, Select, Tabs } from '../components/form';
import { Alert, Badge, Button } from '../components/ui';
import { api } from '../lib/api';
import { HostnameAndDns } from '../components/HostnameAndDns';

interface SystemStatus {
  ami: { connected: boolean };
  config: {
    version: number;
    status: string;
    checksum: string;
    reloads: string[] | null;
    appliedAt: string | null;
    error: string | null;
    drift: boolean;
  } | null;
}

interface Version {
  id: string;
  version: number;
  checksum: string;
  status: string;
  reloadsIssued: string[] | null;
  generatedAt: string;
  appliedAt: string | null;
  error: string | null;
}

interface Preview {
  files: Record<string, string>;
  checksum: string;
  issues: string[];
}

const STATUS_TONE: Record<string, 'success' | 'destructive' | 'warning' | 'muted'> = {
  applied: 'success',
  failed: 'destructive',
  rolled_back: 'warning',
  validating: 'muted',
  pending: 'muted',
};

/** Config history, the generated-config viewer, and the hostname settings. */
export function System() {
  const [tab, setTab] = useState('config');

  return (
    <div className="space-y-6">
      <PageHeader
        title="System"
        description="What this PBX is running, and what it would generate next."
      />

      <Tabs
        tabs={[
          { id: 'config', label: 'Configuration' },
          { id: 'generated', label: 'Generated files' },
          { id: 'hostname', label: 'Hostname and DNS' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'config' && <ConfigHistory />}
      {tab === 'generated' && <GeneratedFiles />}
      {tab === 'hostname' && <HostnameAndDns />}
    </div>
  );
}

function ConfigHistory() {
  const queryClient = useQueryClient();

  const { data: status } = useQuery({
    queryKey: ['system-status'],
    queryFn: () => api.get<SystemStatus>('/api/v1/system/status'),
    refetchInterval: 15_000,
  });

  const { data: versions = [] } = useQuery({
    queryKey: ['config-versions'],
    queryFn: () => api.get<Version[]>('/api/v1/system/config/versions'),
  });

  const apply = useMutation({
    mutationFn: () => api.post('/api/v1/system/config/apply'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['config-versions'] });
      void queryClient.invalidateQueries({ queryKey: ['system-status'] });
    },
  });

  const columns: Array<Column<Version>> = [
    {
      key: 'version',
      header: 'Version',
      sort: (a, b) => a.version - b.version,
      render: (v) => <span className="font-mono tabular-nums">{v.version}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (v) => <Badge tone={STATUS_TONE[v.status] ?? 'muted'}>{v.status}</Badge>,
    },
    {
      key: 'reloads',
      header: 'Reloads issued',
      render: (v) =>
        v.reloadsIssued?.length ? (
          <span className="text-sm">{v.reloadsIssued.join(', ')}</span>
        ) : (
          <span className="text-muted-foreground">none</span>
        ),
    },
    {
      key: 'generatedAt',
      header: 'Generated',
      sort: (a, b) => a.generatedAt.localeCompare(b.generatedAt),
      render: (v) => new Date(v.generatedAt).toLocaleString(),
    },
    {
      key: 'checksum',
      header: 'Checksum',
      render: (v) => (
        <code className="text-xs text-muted-foreground">{v.checksum.slice(0, 12)}</code>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {status?.config?.drift && (
        <Alert tone="warning">
          Asterisk is running configuration version {status.config.version}, which was rolled back
          after a failed apply. The database describes something newer. Nothing is re-applied
          automatically — review the change that failed, fix it, and apply again.
        </Alert>
      )}

      {status?.config?.error && <Alert>{status.config.error}</Alert>}

      <Section
        title="Current state"
        description="Configuration is generated from the database and reloaded into Asterisk."
      >
        <div className="grid gap-4 sm:grid-cols-3">
          <Stat label="AMI" value={status?.ami.connected ? 'Connected' : 'Disconnected'} />
          <Stat label="Config version" value={status?.config ? `v${status.config.version}` : '—'} />
          <Stat
            label="Last applied"
            value={
              status?.config?.appliedAt
                ? new Date(status.config.appliedAt).toLocaleString()
                : 'Never'
            }
          />
        </div>

        <div className="mt-4">
          <Button onClick={() => apply.mutate()} disabled={apply.isPending}>
            <RefreshCw size={14} className={apply.isPending ? 'animate-spin' : undefined} />
            {apply.isPending ? 'Applying…' : 'Regenerate and apply now'}
          </Button>
          <p className="mt-2 text-xs text-muted-foreground">
            Saves apply automatically. This is for forcing a regenerate after editing something
            outside the admin, or after a failed apply.
          </p>
        </div>
      </Section>

      <DataTable
        rows={versions}
        columns={columns}
        rowKey={(v) => v.id}
        search={(v) => `${v.version} ${v.status} ${v.checksum}`}
        searchPlaceholder="Search versions"
        empty="No configuration has been generated yet."
      />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[--radius] border border-border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-medium">{value}</div>
    </div>
  );
}

/**
 * What the generator would write right now.
 *
 * The most useful debugging tool in the product: when a reload misbehaves, reading the exact
 * output turns a guessing game into a diff against what is on disk.
 */
function GeneratedFiles() {
  const [selected, setSelected] = useState('');

  const { data: preview, isLoading } = useQuery({
    queryKey: ['config-preview'],
    queryFn: () => api.get<Preview>('/api/v1/system/config/preview'),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Rendering…</p>;
  if (!preview) return <Alert>Could not render the configuration.</Alert>;

  const names = Object.keys(preview.files).sort();
  const current = selected || names[0] || '';

  return (
    <div className="space-y-4">
      {preview.issues.length > 0 && (
        <Alert tone="warning">
          <div className="font-medium">Validation reported {preview.issues.length} issue(s):</div>
          <ul className="ml-4 mt-1 list-disc">
            {preview.issues.slice(0, 8).map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Section
        title="Generated configuration"
        description={`Checksum ${preview.checksum.slice(0, 16)} · ${names.length} files`}
      >
        <div className="flex items-center gap-2">
          <FileCode2 size={16} className="text-muted-foreground" />
          <div className="w-full max-w-md">
            <Select value={current} onChange={setSelected}>
              {names.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <pre className="mt-4 max-h-[32rem] overflow-auto rounded-[--radius] border border-border bg-muted/40 p-4 text-xs leading-relaxed">
          <code>{preview.files[current] ?? ''}</code>
        </pre>
      </Section>
    </div>
  );
}
